export function toTransactionList(transactions) {
  return Array.isArray(transactions) ? transactions : Object.values(transactions || {});
}

export function normalizeCrossBorderTransaction(transaction) {
  if (!transaction || typeof transaction !== 'object') return transaction;

  const amountIn = transaction.amountSent ?? transaction.amountIn;
  const amountOut = transaction.amountReceived ?? transaction.amountOut;
  const leg1 = transaction.fxSwap ?? transaction.leg1;
  const leg2 = transaction.payout ?? transaction.leg2;

  return {
    ...transaction,
    amountIn,
    amountOut,
    leg1,
    leg2,
  };
}

export function normalizeCrossBorderTransactions(transactions) {
  return toTransactionList(transactions).map(normalizeCrossBorderTransaction);
}

export function getInitials(name) {
  if (!name) return '—';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function isBusinessRecipient(recipient) {
  const type = String(recipient.recipientType || recipient.accountHolderType || recipient.holderType || '').toLowerCase();
  if (['company', 'business', 'corporate', 'organisation', 'organization'].some((token) => type.includes(token))) {
    return true;
  }
  if (type.includes('individual') || type.includes('person')) {
    return false;
  }
  const railType = String(recipient.type || '').toLowerCase();
  if (railType.includes('company') || railType.includes('business')) return true;
  if (railType.includes('individual')) return false;
  const name = String(recipient.name || '').toLowerCase();
  return /\b(ltd|llc|gmbh|inc|corp|bv|sa|ag|plc|co\.)\b/.test(name);
}

export function formatAccountDisplay(recipient) {
  const raw = recipient.iban || recipient.accountNumber || recipient.accountId || recipient.bankAccountNumber || '';
  if (!raw) return '—';
  const normalized = String(raw).replace(/\s/g, '').toUpperCase();
  if (normalized.length >= 8) {
    return normalized.replace(/(.{4})/g, '$1 ').trim();
  }
  return String(raw);
}

export function getBankLabel(recipient) {
  return recipient.bankName || recipient.bank?.name || recipient.financialInstitution || recipient.institutionName || '—';
}

export function getRecipientEmail(recipient) {
  return recipient.email || recipient.emailAddress || recipient.contactEmail || '—';
}

export function getCountryInfo(recipient, configCurrencies) {
  const code =
    recipient.country?.code || recipient.countryCode || recipient.bankCountryCode || recipient.country?.countryCode || null;

  if (recipient.country?.name) {
    return {
      code,
      name: recipient.country.name,
      flag: recipient.country.flag || null,
    };
  }

  const currencyCode = recipient.currency?.toUpperCase();
  const currency = (configCurrencies || []).find((c) => c.code === currencyCode);
  if (currency?.country) {
    return {
      code: currency.country.code || code,
      name: currency.country.name || currency.country.code,
      flag: currency.country.flag || null,
    };
  }
  if (currency?.countries?.length) {
    const country = currency.countries[0];
    return {
      code: country.code || code,
      name: country.name || country.code,
      flag: country.flag || null,
    };
  }

  if (code) {
    return { code, name: code, flag: null };
  }

  return { code: null, name: '—', flag: null };
}

export function formatTransferDate(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatRelativeLastSent(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfDate) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 14) return '1 week ago';
  if (diffDays < 28) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 60) return '1 month ago';
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return formatTransferDate(dateString);
}

import { formatNumberWithDecimalLayout } from './userPreferencesHelpers';

export function formatEuroAmount(amount, preferences) {
  const value = Number(amount) || 0;
  const formatted = formatNumberWithDecimalLayout(value, preferences);
  return `−${formatted} €`;
}

export function formatEuroTotal(amount, preferences) {
  const value = Number(amount) || 0;
  return `${formatNumberWithDecimalLayout(value, preferences)} €`;
}

export function transactionBelongsToRecipient(transaction, recipient) {
  if (!recipient) return false;
  const { id: recipientId, accountId, name } = recipient;
  const nameLower = name?.toLowerCase();
  const txRecipient = transaction.recipient || {};
  if (recipientId && txRecipient.id && String(txRecipient.id) === String(recipientId)) return true;
  if (accountId && txRecipient.accountId && String(txRecipient.accountId) === String(accountId)) return true;
  if (nameLower && txRecipient.name && String(txRecipient.name).toLowerCase() === nameLower) return true;
  return false;
}

export function getTransactionsForRecipient(transactions, recipient) {
  return toTransactionList(transactions)
    .filter((t) => transactionBelongsToRecipient(t, recipient) && (t.started || t.dateAdded))
    .sort((a, b) => new Date(b.started || b.dateAdded) - new Date(a.started || a.dateAdded));
}

export function buildLastSentMap(transactions) {
  const map = new Map();
  toTransactionList(transactions)
    .filter((t) => t.started || t.dateAdded)
    .sort((a, b) => new Date(b.started || b.dateAdded) - new Date(a.started || a.dateAdded))
    .forEach((transaction) => {
      const keys = [transaction.recipient?.id, transaction.recipient?.accountId, transaction.recipient?.name?.toLowerCase()].filter(Boolean);
      keys.forEach((key) => {
        if (!map.has(key)) {
          map.set(key, transaction.started || transaction.dateAdded);
        }
      });
    });
  return map;
}

export function getLastSentDate(recipient, lastSentMap) {
  const keys = [recipient.id, recipient.accountId, recipient.name?.toLowerCase()].filter(Boolean);
  for (let i = 0; i < keys.length; i += 1) {
    const value = lastSentMap.get(keys[i]);
    if (value) return value;
  }
  return null;
}

export function canSendToRecipient(recipient) {
  const hasAccount = Boolean(recipient.iban || recipient.accountNumber || recipient.accountId || recipient.bankAccountNumber);
  const hasCurrency = Boolean(recipient.currency);
  if (!hasAccount || !hasCurrency) return false;
  if (recipient.isValid === false || recipient.status === 'invalid') return false;
  return true;
}

export function isRecipientActive(recipient) {
  if (recipient.isValid === false || recipient.status === 'invalid') return false;
  if (recipient.status === 'inactive' || recipient.status === 'disabled') return false;
  return true;
}

export function formatPaymentPurpose(paymentPurpose) {
  if (!paymentPurpose) return '';
  if (paymentPurpose.includes('_')) {
    return paymentPurpose
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return paymentPurpose.replace(/([A-Z])/g, ' $1').trim();
}

export function getTransactionTitle(transaction) {
  if (transaction.paymentReference) return transaction.paymentReference;
  const purpose = formatPaymentPurpose(transaction.paymentPurpose);
  if (purpose) return purpose;
  return transaction.requestId || 'Transfer';
}

const PENDING_STATUSES = ['Pending', 'Executing', 'Processing'];

export function getTransactionStatusLabel(status) {
  if (status === 'Completed') return 'settled';
  if (PENDING_STATUSES.includes(status)) return 'in transit';
  if (status === 'Failed' || status === 'Cancelled' || status === 'Rejected') return 'failed';
  return (status || '—').toLowerCase();
}

export function getTransactionStatusColor(status) {
  if (status === 'Completed') return 'var(--color-success)';
  if (PENDING_STATUSES.includes(status)) return '#1d4ed8';
  if (status === 'Failed' || status === 'Cancelled' || status === 'Rejected') return 'var(--color-error)';
  return 'var(--text-tertiary)';
}

export function getRecipientCreatedAtMs(recipient) {
  const raw = recipient?.createdAt ?? recipient?.CreatedAt;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
}

export function sortRecipientsByNewest(recipients) {
  if (!Array.isArray(recipients)) return [];
  return [...recipients].sort((a, b) => getRecipientCreatedAtMs(b) - getRecipientCreatedAtMs(a));
}
